/* ---------------------------------------------------------------------
 *                           Import Library
 * --------------------------------------------------------------------- */

// axios
import axios from 'axios';

// Utils
import { HOST_NAME_PORT, LOGIN_PATH, REGISTER_PATH, RESET_PASSWORD_PATH, SMS_PATH } from '../utils/constants';
import { isLoggedIn, getAccessToken, getClientId, isTokenExpired, removeLoginData } from '../data/userData';

/* ---------------------------------------------------------------------
 *                           API Configuration
 * --------------------------------------------------------------------- */

/**
 * 创建API实例
 * @type {object}
 */
const api = axios.create({
    baseURL: HOST_NAME_PORT,
    headers: {
        'Content-Type': 'application/json'
    }
});

/* ---------------------------------------------------------------------
 *                           Request Interceptor
 * --------------------------------------------------------------------- */

/**
 * 请求拦截器
 * @param {Object} config - 请求配置
 * @returns {Object} 请求配置
 */
api.interceptors.request.use(
    (config) => {
        // 如果是登录接口，不添加token和clientId
        if (config.url === LOGIN_PATH || config.url === REGISTER_PATH || config.url === RESET_PASSWORD_PATH || config.url === SMS_PATH) {
            return config;
        }

        // 检查登录状态
        if (!isLoggedIn()) {
            // 清除可能存在的无效数据
            removeLoginData();

            // 重定向到登录页
            window.location.href = '/login';
            return Promise.reject(new Error('未登录或登录已过期'));
        }

        // 获取token和clientId
        const token = getAccessToken();
        const clientId = getClientId();

        // 添加认证头
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['clientid'] = clientId;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/* ---------------------------------------------------------------------
 *                           Response Interceptor
 * --------------------------------------------------------------------- */

/**
 * 响应拦截器
 * @param {Object} response - 响应对象
 * @returns {Object} 响应对象
 */
api.interceptors.response.use(
    (response) => {
        if (response.headers['content-type'] && response.headers['content-type'].includes('text/event-stream')) {
            const { data, status, statusText } = response;

            if (status === 200) {
                return data;
            } else {
                return Promise.reject(new Error(statusText || '操作失败'));
            }
        }

        const { code, message } = response.data;

        if (code !== 200) {
            return Promise.reject(new Error(message || '操作失败'));
        }

        return response.data;
    },
    async (error) => {
        const originalRequest = error.config;

        // 处理401错误（未授权）
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // 检查token是否过期
                if (isTokenExpired()) {
                    // 清除登录数据
                    removeLoginData();

                    // 重定向到登录页
                    window.location.href = '/login';
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                // 清除登录数据
                removeLoginData();

                // 重定向到登录页
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        // 处理403错误（禁止访问）
        if (error.response?.status === 403) {
            return Promise.reject(new Error('没有权限访问该资源'));
        }

        // 处理404错误（资源不存在）
        if (error.response?.status === 404) {
            return Promise.reject(new Error('请求的资源不存在'));
        }

        // 处理500错误（服务器错误）
        if (error.response?.status === 500) {
            return Promise.reject(new Error('服务器内部错误'));
        }

        // 处理网络错误
        if (!error.response) {
            return Promise.reject(new Error('网络连接失败，请检查网络设置'));
        }

        // 处理其他错误
        const errorMessage = error.response?.data?.message || error.message || '请求失败';
        return Promise.reject(new Error(errorMessage));
    }
);

/* ---------------------------------------------------------------------
 *                             API Methods
 * --------------------------------------------------------------------- */

/**
 * 发送GET请求
 * @param {string} url - 请求URL
 * @param {Object} params - 请求参数
 * @param {Object} config - 请求配置
 * @returns {Promise} 请求结果
 */
export const get = (url, params = {}, config = {}) => {
    return api.get(url, { params, ...config });
};

/**
 * 发送POST请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} config - 请求配置
 * @returns {Promise} 请求结果
 */
export const post = (url, data = {}, config = {}) => {
    return api.post(url, data, config);
};

/**
 * 发送PUT请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} config - 请求配置
 * @returns {Promise} 请求结果
 */
export const put = (url, data = {}, config = {}) => {
    return api.put(url, data, config);
};

/**
 * 发送DELETE请求
 * @param {string} url - 请求URL
 * @param {Object} config - 请求配置
 * @returns {Promise} 请求结果
 */
export const del = (url, config = {}) => {
    return api.delete(url, config);
};

/**
 * 发送PATCH请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} config - 请求配置
 * @returns {Promise} 请求结果
 */
export const patch = (url, data = {}, config = {}) => {
    return api.patch(url, data, config);
};

export default api;